import React, { useEffect, useState } from 'react';
import {
  Button,
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
  InlineNotification,
  Tag,
} from '@carbon/react';

import { useCreateVariantGroupMutation } from '@/redux/new_services/variantGroups';
import { showToast } from '@/redux/new_services/toasts';
import { useDispatch } from 'react-redux';

const ORIENTATION_LABEL = {
  0: 'Quer',
  1: 'Hoch',
  2: 'Multi',
};

// slotsForElement turns a selection (single media or pair-aggregated tile)
// into the {landscape_media_id, portrait_media_id} payload of a variant.
// A pair-aggregated tile (_pair_partner present) fills both slots, so the
// resulting variant is itself a Hoch+Quer pair.
function slotsForElement(el) {
  const slots = { landscape_media_id: null, portrait_media_id: null };
  if (!el) return slots;
  if (el.orientation === 0) slots.landscape_media_id = el.model.ID;
  if (el.orientation === 1) slots.portrait_media_id = el.model.ID;
  if (el._pair_partner) {
    if (el._pair_partner.orientation === 0 && !slots.landscape_media_id) {
      slots.landscape_media_id = el._pair_partner.model.ID;
    }
    if (el._pair_partner.orientation === 1 && !slots.portrait_media_id) {
      slots.portrait_media_id = el._pair_partner.model.ID;
    }
  }
  return slots;
}

// CombineSelectionModal — turns a multi-selection into a single variant
// group. Each selected tile becomes one variant; the operator names the
// variants directly in the dialog. The first selection becomes the default.
//
// Pair-aggregated tiles fold their partner into the same variant (both
// orientation slots filled), so a "1 Euro" variant carries both formats.
const CombineSelectionModal = ({ open, elements = [], onClose }) => {
  const dispatch = useDispatch();
  const [createGroup, { isLoading }] = useCreateVariantGroupMutation();

  const [groupName, setGroupName] = useState('');
  const [variantNames, setVariantNames] = useState([]);
  const [error, setError] = useState(null);

  // Re-seed the variant-name list whenever the modal is reopened so the
  // operator starts from the current selection's element names.
  useEffect(() => {
    if (!open) return;
    setGroupName('');
    setVariantNames(elements.map((el) => el.name || ''));
    setError(null);
  }, [open, elements]);

  const updateVariantName = (idx, value) => {
    setVariantNames((cur) => cur.map((n, i) => (i === idx ? value : n)));
  };

  const handleSubmit = async () => {
    const name = groupName.trim();
    if (!name) {
      setError('Bitte einen Gruppen-Namen angeben');
      return;
    }
    if (elements.length < 2) {
      setError('Bitte mindestens zwei Elemente auswählen');
      return;
    }
    if (variantNames.some((n) => !n.trim())) {
      setError('Bitte alle Variantnamen ausfüllen');
      return;
    }
    const variants = elements.map((el, idx) => ({
      name: variantNames[idx].trim(),
      ...slotsForElement(el),
    }));
    try {
      await createGroup({ name, description: '', variants }).unwrap();
      dispatch(showToast({
        kind: 'success',
        title: 'Varianten-Gruppe angelegt',
        subtitle: `${elements.length} Varianten in „${name}" zusammengeführt.`,
      }));
      onClose();
    } catch {
      setError('Anlegen fehlgeschlagen');
    }
  };

  return (
    <ComposedModal open={open} onClose={onClose} size="md">
      <ModalHeader title="Varianten-Gruppe aus Auswahl erzeugen" />
      <ModalBody>
        {error && (
          <InlineNotification
            kind="error"
            title="Fehler"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
            lowContrast
          />
        )}

        <p>
          Aus jedem ausgewählten Element wird eine Variante. Vergib für jede
          Variante einen Namen — z. B. „1 Euro", „2 Euro" oder einen Datums-
          Wert. Die erste Variante wird automatisch als Standard markiert.
        </p>

        <TextInput
          id="combine-group-name"
          labelText="Gruppen-Name"
          placeholder="z. B. Aktion Mai"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
        />

        <ul className="combine-selection-modal__list">
          {elements.map((el, idx) => (
            <li key={el.model.ID} className="combine-selection-modal__row">
              <div className="combine-selection-modal__row-thumb">
                {el.s3_thumb_url ? (
                  <img src={el.s3_thumb_url} alt={el.name} />
                ) : (
                  <div className="combine-selection-modal__row-placeholder" />
                )}
              </div>
              <div className="combine-selection-modal__row-meta">
                <span className="combine-selection-modal__row-name">{el.name}</span>
                <Tag type="cool-gray">{ORIENTATION_LABEL[el.orientation] || '?'}</Tag>
                {el._pair_partner && <Tag type="purple">Pair</Tag>}
                {idx === 0 && <Tag type="green">Standard</Tag>}
              </div>
              <div className="combine-selection-modal__row-input">
                <TextInput
                  id={`combine-variant-${idx}`}
                  labelText="Variantname"
                  hideLabel
                  value={variantNames[idx] || ''}
                  onChange={(e) => updateVariantName(idx, e.target.value)}
                />
              </div>
            </li>
          ))}
        </ul>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>Abbrechen</Button>
        <Button onClick={handleSubmit} disabled={isLoading}>
          Gruppe anlegen
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};

export default CombineSelectionModal;
